import { isEmpty } from 'lodash/fp';

import { FORECAST_SCENARIO_ID_KEY, YEAR_KEY } from '@portal/common/models/data-key';
import { DataResponse } from '@portal/common/types';
import addColumnToDataResponse from '@portal/common/utility/data/add-column-to-data-response';

export type AddForecastScenarioColumnWhenMissingParams = {
    nowcastYears: number[] | null;
    scenarioId: number;
};

/**
 * Adds FORECAST_SCENARIO_ID column to each data response when the column is missing.
 * Fills it by year threshold: record year < min(nowcastYears) → null, else scenarioId
 * (observed vs nowcast split). No-op if nowcastYears is null or empty.
 * Mutates responseByDataTypes in place.
 */
const addForecastScenarioColumnWhenMissing = (
    responseByDataTypes: Record<string, DataResponse>,
    { nowcastYears, scenarioId }: AddForecastScenarioColumnWhenMissingParams
): void => {
    if (isEmpty(nowcastYears)) {
        return;
    }

    for (const [dataType, dataResponse] of Object.entries(responseByDataTypes)) {
        if (!dataResponse?.columns?.includes(FORECAST_SCENARIO_ID_KEY)) {
            const yearIdx = dataResponse.columns.indexOf(YEAR_KEY);
            if (yearIdx === -1) {
                continue;
            }

            const minNowcastYear = Math.min(...nowcastYears);

            responseByDataTypes[dataType] = addColumnToDataResponse(
                dataResponse,
                FORECAST_SCENARIO_ID_KEY,
                (record) => (Number(record[yearIdx]) < minNowcastYear ? null : scenarioId)
            );
        }
    }
};

export default addForecastScenarioColumnWhenMissing;
